// This is called a "splat route" and as it's in the root `/app/routes/`
// directory, it's a catchall. If no other routes match, this one will and we
// can know that the user is hitting a URL that doesn't exist. By throwing a
// 404 from the loader, we can force the error boundary to render which will
// ensure the user gets the right status code and we can display a nicer error
// message for them than the Remix and/or browser default.

import { type LoaderFunctionArgs } from '@remix-run/node'
import { useNavigate } from '@remix-run/react'
import { CarService } from '#app/entities/car/service.server.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { GeneralErrorBoundary } from '#app/shared/ui/error-boundary.js'
import { Icon } from '#app/shared/ui/icons/Icon.js'
import { Button } from '#app/shared/ui/inputs/button/Button.js'
import Header from '#app/shared/ui/layout/Header.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import MainLayout from '#app/shared/ui/layout/MainLayout.js'

export async function loader({ params }: LoaderFunctionArgs) {
  if (params?.carSlug) {
    const exists = await CarService.findBySlug(params.carSlug)
    // if the car exists, means that was sold, so we return a 410 for SEO purposes
    if (exists) {
      throw new Response('Not found', { status: 410 })
    }
  }

  throw new Response('Not found', { status: 404 })
}

export default function NotFound() {
  // due to the loader, this component will never be rendered, but we'll return
  // the error boundary just in case.
  return <ErrorBoundary />
}

export const NotFoundPage = () => {
  const navigate = useNavigate()
  return (
    <MainLayout
      isFixed={true}
      className={`
        font-saira

        lg:h-screen
      `}
    >
      <Header isSticky={true} showBanner={true} />
      <MainContainer className="flex flex-col items-center justify-center">
        <Spacer size="32px" />
        <Spacer sizeMd="32px" />
        <img
          src="/img/Group 240.svg"
          alt="404-error"
          className="h-[94px] w-[94px]"
        />
        <Spacer size="20px" />
        <p
          className={`
            w-full px-7 text-center text-h4 text-neutral-900

            lg:text-h3

            md:px-0

            sm:w-[415px]
          `}
        >
          No hemos encontrado la página que estas buscando.
        </p>
        <Spacer size="16px" />
        <p
          className={`
            w-full px-7 text-center text-base-bold text-neutral-900

            lg:text-h5-bold

            md:px-0

            sm:w-[500px]
          `}
        >
          En caso de tratarse de un anuncio de un automóvil, es probable que
          este ya haya sido vendido.
        </p>
        <Spacer size="20px" />
        <Button
          onClick={() => navigate('/')}
          variant={'outlineInput'}
          className={`
            flex h-12 w-[175px] items-center justify-center gap-4
            border-neutral-900 pl-6 pr-4
          `}
        >
          <p className={`text-nowrap text-base-bold text-neutral-900`}>
            Buscar coches
          </p>
          <Icon
            size="16px"
            className="text-neutral-900"
            name="basic/arrow-right"
          />
        </Button>
        <Spacer size="20px" />
        <p
          className={`
            text-subtitle1-medium text-neutral-500

            lg:mb-[2px]
          `}
        >
          Del mundo a la puerta de tu casa.
        </p>
        <Spacer size="56px" />
        <Spacer sizeMd="80px" />
        <Spacer sizeMd="4px" />
      </MainContainer>
    </MainLayout>
  )
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        404: () => <NotFoundPage />,
        410: () => <NotFoundPage />,
      }}
    />
  )
}
